import React, { FC, useContext, useEffect } from 'react'

import {
  ActiveStoryContext,
  HeadingsContextProvider,
} from '@izettle/public-site-components/dist/help-center'
import { getBlokContent } from '@app/utils/blok-content'
import { isNonEmptyArray } from '@app/utils/type-check'
import { SearchOverlayWrapper } from '@app/components/search-overlay-wrapper'
import { Blok } from '@app/typings/blok'
import { supportedQueryParameters } from '@app/utils/query-parameters-utils'
import StoryInfo from './StoryInfo'
import { StoryblokComponent } from '@storyblok/react'

const HeadingNavigation: FC<any> = ({ className, headingNavigation }) => {
  return (
    <div className={className}>
      {headingNavigation && (
        <StoryblokComponent
          blok={headingNavigation[0]}
          {...headingNavigation[0]}
        />
      )}
    </div>
  )
}

type Props = {
  navigation: Blok[]
  headingNavigation: Blok[]
  header: Blok[]
  statusBanner: Blok[]
  footer: Blok[]
  search: Blok[]
  title: string
  path: string
  articleFeedback: Blok[]
  articleId: string
  fullSlug: string
  queryParameters: supportedQueryParameters
  articleFullSlug?: string | null
}

const getChildrenProps = (child: React.ReactNode): any => {
  if (React.isValidElement(child)) {
    return child.props
  }

  return {}
}

export const LayoutArticle: FC<Props> = ({
  navigation,
  headingNavigation,
  header,
  statusBanner,
  footer = [],
  search = [],
  title,
  path = '',
  children,
  articleFeedback,
  articleId,
  fullSlug,
  queryParameters,
  articleFullSlug,
}) => {
  const { setActiveStory } = useContext(ActiveStoryContext)

  useEffect(() => {
    setActiveStory(path)
  }, [setActiveStory, path])

  const hideFooter = queryParameters.hideFooter
  const hideRightSidebar = queryParameters.hideRightSidebar
  const hideHeader = queryParameters.hideHeader

  const badgeGroup = getChildrenProps(children).badgeGroup

  return (
    <div className="wrapper" style={{ minHeight: '100vh' }}>
      <SearchOverlayWrapper {...search[0]} />
      {!hideHeader && header && (
        <StoryblokComponent
          blok={{
            ...header[0],
            articleNavigation: getBlokContent(navigation),
            initialActiveStory: path,
          }}
          {...{
            ...header[0],
            articleNavigation: getBlokContent(navigation),
            initialActiveStory: path,
          }}
        />
      )}
      <main role="main" className="main public-web-wrapper help-center-wrapper">
        {articleFullSlug && <StoryInfo articleFullSlug={articleFullSlug} />}
        {isNonEmptyArray(statusBanner) && (
          <StoryblokComponent
            blok={getBlokContent(statusBanner)}
            {...getBlokContent(statusBanner)}
          />
        )}
        <div className="flex-container section-normal py-extra">
          {isNonEmptyArray(navigation) && (
            <div className="sidebar-left">
              <StoryblokComponent
                blok={{ ...getBlokContent(navigation), initialActiveStory: path }}
                { ...getBlokContent(navigation) }
                initialActiveStory={path}
              />
            </div>
          )}

          <HeadingsContextProvider>
            <div className="content sb-indexed__body">
              <div className="article-title-container">
                <h1>{title}</h1>
                {isNonEmptyArray(badgeGroup) && (
                  <StoryblokComponent
                    blok={getBlokContent(badgeGroup)}
                    {...getBlokContent(badgeGroup)}
                  />
                )}
              </div>
              {!hideRightSidebar && (
                <HeadingNavigation
                  headingNavigation={headingNavigation}
                  className="d-lg-none d-xl-none"
                />
              )}
              {children}

              {isNonEmptyArray(articleFeedback) && (
                <StoryblokComponent
                  blok={getBlokContent(articleFeedback)}
                  {...{
                    ...getBlokContent(articleFeedback),
                    path,
                    articleId,
                    fullSlug,
                  }}
                />
              )}
            </div>

            {!hideRightSidebar && (
              <HeadingNavigation
                title={title}
                headingNavigation={headingNavigation}
                className="sidebar-right"
              />
            )}
          </HeadingsContextProvider>
        </div>
      </main>

      {!hideFooter && (
        <div className="bottom">
          {footer?.map((block) => (
              <StoryblokComponent key={block._uid} blok={getBlokContent([block])} {...getBlokContent([block])} />
            ))}
        </div>
      )}
    </div>
  )
}
